import { Button } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import ReactFlow, {
  DefaultEdgeOptions,
  Edge,
  MarkerType,
  Node,
  NodeTypes,
  OnConnect,
  Panel,
  SelectionMode,
  useReactFlow,
  XYPosition,
} from 'reactflow';

import 'reactflow/dist/style.css';
import type { TapeBuilderNodeType } from '@finley-technologies/tape-builder';

import { DownloadIconButton, IconButton, SaveIconButton } from '../IconButton';
import { FileUploadButton } from './FileUploadButton';
import { repositionGraph } from './layout/d3hierarchy';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';

export type { NodeTypes };

// every reactFlow node has an id, type, data, and position
// {
//   // identifier for the node
//   id: string;
//
//   // type of the node. this is generic
//   // you would typically use 'artifactType' for nodeType.
//   type: U;

//   // node data can be generic, but
//   // instead of letting reactFlow manage our node data
//   // we let it only store the nodeIds, and manage the data
//   // ourselfs. hence our data is always string.
//   data: string;
//
//   // we will let reactFlow manage the position of the node.
//   // and only supply it with store for the position.
//   position: XYPosition;
// }

export type InternalNode<U extends string> = Node<string, U>;

export type ExternalNode<U extends string> = {
  id: string;
  type: U;
};

export type ReactFlowWrapperEdge = Edge;

interface ReactFlowWrapperControlProps {
  topRight: React.ReactNode;
  topLeft: React.ReactNode;
  elementRef: React.RefObject<HTMLDivElement>;
  nodeTypes: NodeTypes;

  // suppply this callback if you want to allow edge addition/deletion.
  onConnect?: OnConnect;
  onEdgeDeleted?: (edgeToDelete: Edge) => void;
  onEdgesDeleted?: (edgesToDelete: Edge[]) => void;
  onNodesDeleted?: (nodesToDelete: Node[]) => void;

  // supply this call back if you want to drop new nodes on the graph
  // this should return a new node id.
  onNewNode?: (nodeType: TapeBuilderNodeType, position: XYPosition) => void;

  // handle importing graph.
  handleImport: (file: File) => Promise<void>;

  // hanlde merge import
  handleImportMerge: (file: File) => Promise<void>;

  handleExport: () => void;

  handleSavePositions?: () => void;

  selectedEdge: Edge | null;
}

const defaultViewport = { x: 0, y: 0, zoom: 2 }; // start somewhat zoomed out.

const defaultEdgeOptions: DefaultEdgeOptions = {
  animated: true,
  style: { strokeWidth: 2 }, // Adjust strokeWidth and stroke color here

  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
  },
};

export const ReactFlowWrapperControl = ({
  topLeft,
  topRight,
  elementRef,
  nodeTypes,
  onConnect,
  onEdgeDeleted,
  onEdgesDeleted,
  onNodesDeleted,
  onNewNode,
  handleExport,
  handleImport,
  handleImportMerge,
  handleSavePositions,
  selectedEdge,
}: ReactFlowWrapperControlProps) => {
  const reactFlowInstance = useReactFlow();

  const onRepositionGraph = useCallback(() => {
    const nodes = reactFlowInstance.getNodes();
    const edges = reactFlowInstance.getEdges();

    const positionedNodes = repositionGraph({
      nodes,
      edges,
      xSpacing: 300,
      ySpacing: 300,
      disconnectedSpacing: 400,
    });

    reactFlowInstance.setNodes(positionedNodes);
    reactFlowInstance.fitView({
      duration: 1000,
      minZoom: 0.1,
      maxZoom: 1,
    });
  }, [reactFlowInstance]);

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (onNewNode) {
        event.preventDefault();
        if (elementRef.current && reactFlowInstance) {
          const reactFlowBounds = elementRef.current.getBoundingClientRect();
          const type = event.dataTransfer.getData(
            'application/reactflow',
          ) as TapeBuilderNodeType;
          const position = reactFlowInstance.project({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
          });
          onNewNode(type, position);
        }
      }
    },
    [elementRef, onNewNode, reactFlowInstance],
  );

  return (
    <>
      {/* Here we are using ReactFlow as an 'uncontrolled' component. We do not pass any nodes or
      edges to it. Instead, we can update the internal state of the component with the methods found
      on reactFlowInstance. */}
      <ReactFlow
        disableKeyboardA11y
        defaultNodes={[]}
        defaultEdges={[]}
        nodeTypes={nodeTypes}
        elevateEdgesOnSelect
        defaultEdgeOptions={defaultEdgeOptions}
        onConnect={onConnect}
        onDragOver={onDragOver}
        onDrop={onDrop}
        defaultViewport={defaultViewport}
        fitView
        minZoom={0.1}
        maxZoom={50}
        proOptions={{
          hideAttribution: true,
        }}
        selectionMode={SelectionMode.Partial}
        tabIndex={0}
        onEdgesDelete={onEdgesDeleted}
        onNodesDelete={onNodesDeleted}
        connectionRadius={40}
        onlyRenderVisibleElements
      >
        {topLeft && <Panel position="top-left">{topLeft}</Panel>}
        {topRight && <Panel position="top-right">{topRight}</Panel>}
        {selectedEdge && onEdgeDeleted && (
          <Panel position="bottom-left">
            <Button
              onClick={() => {
                onEdgeDeleted(selectedEdge);
              }}
            >
              Delete Edge
            </Button>
          </Panel>
        )}
        <Panel position="bottom-right">
          {handleExport && (
            <DownloadIconButton
              onClick={() => handleExport()}
              ariaLabel="Export"
              label="Export"
            />
          )}
          {handleImport && (
            <FileUploadButton
              icon={<FaIcon icon="file-import" />}
              allowedExtensions={['json']}
              modalHeaderText="Overwrite"
              handleFile={async (file: File) => {
                await handleImport(file);
              }}
            />
          )}
          {handleImportMerge && (
            <FileUploadButton
              icon={<FaIcon icon="plus" />}
              allowedExtensions={['json']}
              modalHeaderText="Add"
              handleFile={async (file: File) => {
                await handleImportMerge(file);
              }}
            />
          )}
          {handleSavePositions && (
            <SaveIconButton
              onClick={() => handleSavePositions()}
              ariaLabel="Save Layout"
              label="Save Layout"
            />
          )}

          <IconButton
            ariaLabel="Reposition"
            label="Reposition"
            icon={<FaIcon icon="arrows-rotate-reverse" />}
            onClick={onRepositionGraph}
          >
            Reposition
          </IconButton>
        </Panel>
      </ReactFlow>
    </>
  );
};
